import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Mic, Square, Loader2, RefreshCw, Check, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useSpeakingPractice } from "@/hooks/useSpeakingPractice";
import { isPaymentUiBlocked } from "@/lib/platform";

interface SpeakingPhaseProps {
  targetText: string;
  language: string;
  sessionId: string;
  onComplete: () => void;
  disabled?: boolean;
}

const BAR_COUNT = 5;

function scoreColor(score: number): string {
  if (score >= 80) return "hsl(140 60% 55%)";
  if (score >= 50) return "hsl(38 92% 60%)";
  return "hsl(0 70% 60%)";
}

function WaveformBars({ active }: { active: boolean }) {
  const reduceMotion = useReducedMotion();
  const [heights, setHeights] = useState<number[]>(() => Array(BAR_COUNT).fill(0.3));
  useEffect(() => {
    if (!active || reduceMotion) return;
    const id = window.setInterval(() => {
      setHeights(Array.from({ length: BAR_COUNT }, () => 0.3 + Math.random() * 0.7));
    }, 140);
    return () => window.clearInterval(id);
  }, [active, reduceMotion]);
  return (
    <div className="flex items-center justify-center gap-1.5 h-12">
      {heights.map((h, i) => (
        <motion.span
          key={i}
          className="w-1.5 rounded-full"
          animate={{ height: `${h * 40 + 6}px` }}
          transition={{ duration: 0.16, ease: "easeOut" }}
          style={{ background: "hsl(187 72% 55%)", opacity: 0.55 + h * 0.4 }}
        />
      ))}
    </div>
  );
}

const SpeakingPhase = ({ targetText, language, sessionId, onComplete, disabled = false }: SpeakingPhaseProps) => {
  const { t } = useLanguage();
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const {
    status,
    score,
    transcription,
    error,
    quotaInfo,
    recordingSeconds,
    startRecording,
    stopRecording,
    reset,
  } = useSpeakingPractice({ language, sessionId });

  const countdown = Math.max(0, 15 - recordingSeconds);

  const scoreLabelKey = useMemo(() => {
    if (score == null) return null;
    if (score >= 80) return "language.speaking.excellent";
    if (score >= 50) return "language.speaking.good";
    return "language.speaking.tryAgain";
  }, [score]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="w-full rounded-2xl p-6"
      style={{
        background: "hsl(var(--card) / 0.7)",
        border: "1.5px solid hsl(187 72% 50% / 0.25)",
        boxShadow: "0 0 30px hsl(187 72% 50% / 0.08)",
        backdropFilter: "blur(12px)",
      }}
    >
      <p className="text-[9px] font-display tracking-[0.18em] uppercase text-center mb-1" style={{ color: "hsl(187 72% 55%)" }}>
        {t("language.speaking.title")}
      </p>

      {/* Target sentence */}
      <p className="font-display text-[22px] font-bold text-foreground leading-snug text-center mt-4 mb-6 px-2">
        {targetText}
      </p>

      <AnimatePresence mode="wait">
        {status === "idle" && (
          <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
            <motion.button disabled={disabled}
              type="button"
              onClick={() => startRecording(targetText)}
              className="w-20 h-20 rounded-full flex items-center justify-center active:scale-95"
              style={{
                background: "hsl(187 72% 50%)",
                boxShadow: "0 0 28px hsl(187 72% 50% / 0.45), 0 0 0 8px hsl(187 72% 50% / 0.12)",
                color: "hsl(222 38% 6%)",
              }}
              animate={reduceMotion ? undefined : { scale: [1, 1.04, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              aria-label={t("language.speaking.title")}
            >
              <Mic size={28} />
            </motion.button>
            <p className="text-[12px] font-body text-foreground/60 text-center">
              {t("language.speaking.instruction")}
            </p>
          </motion.div>
        )}

        {status === "recording" && (
          <motion.div key="recording" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
            <WaveformBars active />
            <p className="text-[11px] font-display tracking-wide text-foreground/70">
              {t("language.speaking.recording")} · {countdown}s
            </p>
            <button disabled={disabled}
              type="button"
              onClick={stopRecording}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-display font-semibold text-[13px] active:scale-[0.97]"
              style={{
                background: "hsl(0 70% 55%)",
                color: "hsl(0 0% 100%)",
                boxShadow: "0 0 20px hsl(0 70% 55% / 0.25)",
              }}
            >
              <Square size={14} />
              {t("language.speaking.recording")}
            </button>
          </motion.div>
        )}

        {status === "processing" && (
          <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-3">
            <Loader2 size={28} className="animate-spin" style={{ color: "hsl(187 72% 60%)" }} />
            <p className="text-[12px] font-body text-foreground/65">{t("language.speaking.processing")}</p>
          </motion.div>
        )}

        {status === "success" && score != null && (
          <motion.div key="success" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-3">
            <p className="font-display text-[52px] font-bold leading-none" style={{ color: scoreColor(score) }}>
              {score}
            </p>
            {scoreLabelKey && (
              <p className="text-[13px] font-display font-semibold" style={{ color: scoreColor(score) }}>
                {t(scoreLabelKey)}
              </p>
            )}
            {transcription && (
              <p className="text-[11px] font-body italic text-foreground/55 text-center leading-relaxed px-2">
                "{transcription}"
              </p>
            )}
            <button disabled={disabled}
              type="button"
              onClick={onComplete}
              className="mt-2 w-full py-3 rounded-xl font-display font-semibold text-[13px] active:scale-[0.97]"
              style={{
                background: "hsl(187 72% 50%)",
                color: "hsl(222 38% 6%)",
              }}
            >
              {t("common.continue")}
            </button>
          </motion.div>
        )}

        {status === "error" && (
          <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "hsl(0 70% 50% / 0.15)", border: "1px solid hsl(0 70% 55% / 0.35)" }}
            >
              <X size={18} style={{ color: "hsl(0 70% 65%)" }} />
            </div>
            <p className="text-[12px] font-body text-foreground/70 text-center">
              {error || t("common.error")}
            </p>
            <div className="flex gap-2 w-full">
              <button disabled={disabled}
                type="button"
                onClick={reset}
                className="flex-1 py-2.5 rounded-xl font-display font-semibold text-[12px] inline-flex items-center justify-center gap-1.5"
                style={{
                  background: "hsl(187 72% 50% / 0.15)",
                  color: "hsl(187 72% 65%)",
                  border: "1px solid hsl(187 72% 50% / 0.35)",
                }}
              >
                <RefreshCw size={12} />
                {t("language.speaking.retry")}
              </button>
              <button disabled={disabled}
                type="button"
                onClick={onComplete}
                className="flex-1 py-2.5 rounded-xl font-display font-semibold text-[12px]"
                style={{
                  background: "hsl(222 18% 18% / 0.6)",
                  color: "hsl(210 20% 85%)",
                  border: "1px solid hsl(222 18% 30% / 0.6)",
                }}
              >
                {t("language.speaking.continueWithoutVoice")}
              </button>
            </div>
          </motion.div>
        )}

        {status === "quota" && (
          <motion.div key="quota" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-3">
            <p className="font-display text-[14px] font-bold text-foreground">
              {t("language.speaking.quotaTitle")}
            </p>
            {quotaInfo?.tier === "free" && !isPaymentUiBlocked() && (
              <p className="text-[12px] font-body text-foreground/65 text-center">
                {t("language.speaking.quotaUpsellPremium")}
              </p>
            )}
            <div className="flex gap-2 w-full">
              {quotaInfo?.tier === "free" && !isPaymentUiBlocked() && (
                <button disabled={disabled}
                  type="button"
                  onClick={() => navigate("/pricing")}
                  className="flex-1 py-2.5 rounded-xl font-display font-semibold text-[12px] inline-flex items-center justify-center gap-1.5"
                  style={{
                    background: "hsl(187 72% 50%)",
                    color: "hsl(222 38% 6%)",
                  }}
                >
                  <Check size={12} />
                  {t("language.speaking.quotaUpsellPremium")}
                </button>
              )}
              <button disabled={disabled}
                type="button"
                onClick={onComplete}
                className="flex-1 py-2.5 rounded-xl font-display font-semibold text-[12px]"
                style={{
                  background: "hsl(222 18% 18% / 0.6)",
                  color: "hsl(210 20% 85%)",
                  border: "1px solid hsl(222 18% 30% / 0.6)",
                }}
              >
                {t("language.speaking.continueWithoutVoice")}
              </button>
            </div>
          </motion.div>
        )}

        {status === "mic_permission" && (
          <motion.div key="mic" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-3">
            <p className="text-[12px] font-body text-foreground/70 text-center leading-relaxed">
              {t("language.speaking.micPermission")}
            </p>
            <div className="flex gap-2 w-full">
              <button disabled={disabled}
                type="button"
                onClick={() => startRecording(targetText)}
                className="flex-1 py-2.5 rounded-xl font-display font-semibold text-[12px] inline-flex items-center justify-center gap-1.5"
                style={{
                  background: "hsl(187 72% 50%)",
                  color: "hsl(222 38% 6%)",
                }}
              >
                <Mic size={12} />
                {t("language.speaking.retry")}
              </button>
              <button disabled={disabled}
                type="button"
                onClick={onComplete}
                className="flex-1 py-2.5 rounded-xl font-display font-semibold text-[12px]"
                style={{
                  background: "hsl(222 18% 18% / 0.6)",
                  color: "hsl(210 20% 85%)",
                  border: "1px solid hsl(222 18% 30% / 0.6)",
                }}
              >
                {t("language.speaking.continueWithoutVoice")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SpeakingPhase;
